/* eslint-disable @typescript-eslint/no-explicit-any -- d3 selections and simulation nodes are loosely typed */
import { useEffect, useMemo, useRef } from 'react';
import { forceSimulation, forceManyBody, forceLink, forceCollide, forceCenter, type Simulation } from 'd3-force';
import { select } from 'd3-selection';
import { zoom as d3zoom, zoomIdentity, type ZoomTransform } from 'd3-zoom';
import { drag as d3drag } from 'd3-drag';
import type { VizGraph } from './types';
import { measureCard, drawCardAt, SELECTED_COLOR } from './cardNode';
import { useGraphTheme } from './graphTheme';
import { highlightSet, linkActive } from './highlight';

export interface FocusRequest {
  id: string;
  /** Bump to re-centre on the same node. */
  nonce: number;
}

interface Props {
  data: VizGraph;
  onSelect: (id: string) => void;
  selectedId?: string | null;
  /** Node pinned to the centre and drawn red (e.g. the source of a connection). */
  pinnedId?: string | null;
  /** Called on a click that hits no node. */
  onBackgroundClick?: () => void;
  focus?: FocusRequest | null;
  cursor?: string;
}

const S = 0.3; // px → world-unit scale for cards

interface SimNode {
  id: string;
  label: string;
  type: string;
  color: string;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  __w: number;
  __h: number;
  __r: number;
}

interface SimLink {
  source: SimNode | string;
  target: SimNode | string;
  label: string;
  color?: string;
}

/** Olisé force-directed canvas graph: card nodes, drag-to-pin, neighbour highlight. */
export function Graph2D({ data, onSelect, selectedId, pinnedId, onBackgroundClick, focus, cursor = 'grab' }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useGraphTheme();
  const highlight = useMemo(() => highlightSet(data, selectedId), [data, selectedId]);

  // Mutable refs the render loop and d3 handlers read without re-subscribing.
  const transformRef = useRef<ZoomTransform>(zoomIdentity);
  const themeRef = useRef(theme);
  const selectedRef = useRef(selectedId);
  const highlightRef = useRef(highlight);
  const pinnedRef = useRef(pinnedId);
  const onSelectRef = useRef(onSelect);
  const onBackgroundRef = useRef(onBackgroundClick);
  themeRef.current = theme;
  selectedRef.current = selectedId;
  highlightRef.current = highlight;
  pinnedRef.current = pinnedId;
  onSelectRef.current = onSelect;
  onBackgroundRef.current = onBackgroundClick;

  const simRef = useRef<Simulation<SimNode, SimLink> | null>(null);
  const nodesRef = useRef<SimNode[]>([]);
  const linksRef = useRef<SimLink[]>([]);
  const fittedRef = useRef(false);
  const zoomBehaviorRef = useRef<ReturnType<typeof d3zoom<HTMLCanvasElement, unknown>> | null>(null);

  useEffect(() => {
    // Keep positions of nodes that survive a data change so toggling types doesn't reshuffle.
    const prev = new Map(nodesRef.current.map((n) => [n.id, n]));
    const nodes: SimNode[] = data.nodes.map((n) => {
      const card = measureCard(n.label, n.type);
      const old = prev.get(n.id);
      return {
        ...n,
        x: old?.x ?? (Math.random() - 0.5) * 400,
        y: old?.y ?? (Math.random() - 0.5) * 400,
        fx: n.id === pinnedId ? old?.x ?? 0 : old?.fx ?? null,
        fy: n.id === pinnedId ? old?.y ?? 0 : old?.fy ?? null,
        __w: card.w * S,
        __h: card.h * S,
        __r: Math.hypot(card.w, card.h) * 0.5 * S + 6,
      };
    });
    const byId = new Set(nodes.map((n) => n.id));
    const links: SimLink[] = data.links
      .filter((l) => l.source !== l.target && byId.has(l.source) && byId.has(l.target))
      .map((l) => ({ ...l }));

    nodesRef.current = nodes;
    linksRef.current = links;
    // Refit only for a different graph; a filter change keeps the user's viewport.
    if (!nodes.some((n) => prev.has(n.id))) fittedRef.current = false;
    // A label edit keeps the same nodes: nudge the layout instead of re-running it.
    const sameNodes = prev.size === nodes.length && nodes.every((n) => prev.has(n.id));

    const sim = forceSimulation<SimNode>(nodes)
      .force('charge', forceManyBody<SimNode>().strength(-160).distanceMax(600))
      .force('link', forceLink<SimNode, SimLink>(links).id((d) => d.id).distance(70).strength(0.4))
      .force('collide', forceCollide<SimNode>((n) => n.__r || 30).iterations(2))
      .force('center', forceCenter(0, 0))
      .alpha(sameNodes ? 0.05 : prev.size > 0 ? 0.5 : 1)
      .alphaDecay(0.0228);

    simRef.current = sim;
    let ticks = 0;
    sim.on('tick', () => {
      // Fit early so the graph isn't drawn off-screen while the layout settles.
      if (++ticks === 60) fitToView(false);
      draw();
    });
    sim.on('end', () => fitToView(true));
    return () => {
      sim.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pinnedId]);

  function draw() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const t = transformRef.current;
    const th = themeRef.current;
    const sel = selectedRef.current;
    const hl = highlightRef.current;
    const pin = pinnedRef.current;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;

    ctx.save();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = th.bg;
    ctx.fillRect(0, 0, w, h);
    ctx.translate(t.x, t.y);
    ctx.scale(t.k, t.k);

    const labelled: SimLink[] = [];
    for (const l of linksRef.current) {
      const s = l.source as SimNode;
      const tg = l.target as SimNode;
      if (typeof s !== 'object' || typeof tg !== 'object') continue;
      const active = linkActive({ source: s.id, target: tg.id }, sel);
      const color = l.color ?? th.link;
      ctx.globalAlpha = active ? 1 : l.color ? 0.25 : 1;
      ctx.strokeStyle = active || l.color ? color : th.linkDim;
      ctx.lineWidth = (sel && active ? 1.5 : 1) / t.k;
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(tg.x, tg.y);
      ctx.stroke();
      if (active) drawArrow(ctx, s, tg, color, t.k);
      if (sel && active && l.label) labelled.push(l);
    }
    ctx.globalAlpha = 1;

    for (const node of nodesRef.current) {
      const card = measureCard(node.label, node.type);
      ctx.save();
      ctx.globalAlpha = hl && !hl.has(node.id) ? 0.5 : 1;
      ctx.translate(node.x, node.y);
      ctx.scale(S, S);
      drawCardAt(ctx, 0, 0, card, {
        color: node.id === pin ? SELECTED_COLOR : node.color,
        type: node.type,
        selected: node.id === sel,
        borderColor: th.border,
      });
      ctx.restore();
    }

    // Relation names only for the selected node's links, on top of the cards.
    ctx.font = `500 ${11 / Math.max(t.k, 0.6)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const l of labelled) {
      const s = l.source as SimNode;
      const tg = l.target as SimNode;
      const mx = (s.x + tg.x) / 2;
      const my = (s.y + tg.y) / 2;
      const tw = ctx.measureText(l.label).width;
      const pad = 3 / t.k;
      const fh = 14 / Math.max(t.k, 0.6);
      ctx.fillStyle = th.bg;
      ctx.globalAlpha = 0.85;
      ctx.fillRect(mx - tw / 2 - pad, my - fh / 2, tw + pad * 2, fh);
      ctx.globalAlpha = 1;
      ctx.fillStyle = l.color ?? th.text;
      ctx.fillText(l.label, mx, my);
    }
    ctx.restore();
  }

  function drawArrow(ctx: CanvasRenderingContext2D, s: SimNode, t: SimNode, color: string, k: number) {
    const dx = t.x - s.x;
    const dy = t.y - s.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    // Land the arrow on the target card's edge, not its centre.
    const back = Math.max(t.__w, t.__h) * 0.55;
    const tipX = t.x - ux * back;
    const tipY = t.y - uy * back;
    const size = 4 / k;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(tipX - ux * size * 2 + uy * size, tipY - uy * size * 2 - ux * size);
    ctx.lineTo(tipX - ux * size * 2 - uy * size, tipY - uy * size * 2 + ux * size);
    ctx.closePath();
    ctx.fill();
  }

  function viewSize(): [number, number] {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    return canvas ? [canvas.width / dpr, canvas.height / dpr] : [0, 0];
  }

  function applyTransform(t: ZoomTransform) {
    const canvas = canvasRef.current;
    if (!canvas || !zoomBehaviorRef.current) return;
    zoomBehaviorRef.current.transform(select(canvas) as any, t);
  }

  function fitToView(final: boolean) {
    const nodes = nodesRef.current;
    if (nodes.length === 0 || fittedRef.current) return;
    const [w, h] = viewSize();
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      minX = Math.min(minX, n.x - n.__w / 2);
      minY = Math.min(minY, n.y - n.__h / 2);
      maxX = Math.max(maxX, n.x + n.__w / 2);
      maxY = Math.max(maxY, n.y + n.__h / 2);
    }
    const k = Math.min(2, 0.9 * Math.min(w / (maxX - minX || 1), h / (maxY - minY || 1)));
    if (final) fittedRef.current = true;
    applyTransform(zoomIdentity.translate(w / 2 - (k * (minX + maxX)) / 2, h / 2 - (k * (minY + maxY)) / 2).scale(k));
  }

  useEffect(() => {
    if (!focus) return;
    const node = nodesRef.current.find((n) => n.id === focus.id);
    if (!node) return;
    const [w, h] = viewSize();
    const k = Math.max(transformRef.current.k, 1.5);
    fittedRef.current = true;
    applyTransform(zoomIdentity.translate(w / 2 - k * node.x, h / 2 - k * node.y).scale(k));
  }, [focus]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const sel = select(canvas);

    const pointerGraph = (event: any): [number, number] => {
      const rect = canvas.getBoundingClientRect();
      const ev = event.sourceEvent ?? event;
      return transformRef.current.invert([ev.clientX - rect.left, ev.clientY - rect.top]);
    };

    const nodeAt = ([px, py]: [number, number]): SimNode | undefined => {
      const nodes = nodesRef.current;
      for (let i = nodes.length - 1; i >= 0; i--) {
        const n = nodes[i];
        if (px >= n.x - n.__w / 2 && px <= n.x + n.__w / 2 && py >= n.y - n.__h / 2 && py <= n.y + n.__h / 2) {
          return n;
        }
      }
      return undefined;
    };

    const zoomBehavior = d3zoom<HTMLCanvasElement, unknown>()
      .scaleExtent([0.05, 8])
      // Drag handles gestures that start on a node; zoom/pan handles the rest.
      .filter((event) => event.type === 'wheel' || !nodeAt(pointerGraph(event)))
      .on('zoom', (event) => {
        // A user pan/zoom wins over the automatic fit.
        if (event.sourceEvent) fittedRef.current = true;
        transformRef.current = event.transform;
        draw();
      });
    zoomBehaviorRef.current = zoomBehavior;

    let dragStart: [number, number] | null = null;
    let moved = false;

    const dragBehavior = d3drag<HTMLCanvasElement, unknown>()
      .subject((event) => nodeAt(pointerGraph(event)) ?? null)
      .on('start', (event) => {
        const subj = event.subject as SimNode;
        dragStart = [event.x, event.y];
        moved = false;
        const [gx, gy] = pointerGraph(event);
        subj.fx = gx;
        subj.fy = gy;
      })
      .on('drag', (event) => {
        const subj = event.subject as SimNode;
        if (!moved && dragStart && Math.hypot(event.x - dragStart[0], event.y - dragStart[1]) > 3) {
          moved = true;
          simRef.current?.alphaTarget(0.3).restart();
        }
        const [gx, gy] = pointerGraph(event);
        subj.fx = gx;
        subj.fy = gy;
        if (!moved) draw();
      })
      .on('end', (event) => {
        const subj = event.subject as SimNode;
        simRef.current?.alphaTarget(0);
        if (moved) {
          // Pinned where dropped until dragged again.
          subj.fx = subj.x;
          subj.fy = subj.y;
        } else {
          if (subj.id !== pinnedRef.current) {
            subj.fx = null;
            subj.fy = null;
          }
          onSelectRef.current(subj.id);
        }
      });

    sel.call(dragBehavior as any).call(zoomBehavior as any);
    sel.on('dblclick.zoom', null);
    // d3-zoom swallows the click that ends a pan, so this only fires on real clicks.
    sel.on('click.background', (event: MouseEvent) => {
      if (!nodeAt(pointerGraph(event))) onBackgroundRef.current?.();
    });

    return () => {
      sel.on('.zoom', null).on('.drag', null).on('click.background', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, selectedId, highlight, pinnedId]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ro = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      // Until the first fit, keep the layout origin at the centre of the canvas.
      if (!fittedRef.current) {
        const centred = zoomIdentity.translate(w / 2, h / 2).scale(transformRef.current.k);
        if (zoomBehaviorRef.current) applyTransform(centred);
        else transformRef.current = centred;
      }
      draw();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapRef} className="h-full w-full">
      <canvas ref={canvasRef} className="block h-full w-full" style={{ cursor }} />
    </div>
  );
}
